/**
 * Política central de roles y acceso al UI.
 *
 * Roles válidos: superadmin · direccion · gerente · cabina · coordinador
 *               · paramedico · operador · medico · enfermera · finanzas
 *               · comercial · auditor · ambulancia · cliente
 *
 * Grupos:
 *   - ADMIN  : superadmin, direccion
 *   - FULL_OPS: ADMIN + gerente, coordinador, cabina, dispatch
 *   - FINANCE: ADMIN + finanzas, comercial
 *   - AUDIT  : ADMIN + auditor
 *   - INTERNO_OPERATIVO: paramedico, operador, medico, enfermera (vista similar a cabina pero limitada)
 *   - AMBULANCIA: solo la unidad montada en tablet (tripulación a bordo)
 *   - CLIENTE: portal corporativo (NO entra al UI interno)
 */

export const ROLE_GROUPS = {
    ADMIN: new Set(["superadmin", "direccion"]),
    FULL_OPS: new Set(["superadmin", "direccion", "gerente", "coordinador", "cabina"]),
    FINANCE: new Set(["superadmin", "direccion", "finanzas", "comercial"]),
    AUDIT: new Set(["superadmin", "direccion", "auditor"]),
    INTERNO: new Set(["superadmin", "direccion", "gerente", "coordinador", "cabina",
        "paramedico", "operador", "medico", "enfermera", "finanzas", "comercial", "auditor"]),
    AMBULANCIA: new Set(["ambulancia"]),
    CLIENTE: new Set(["cliente"]),
};

export const ROLE_LABELS = {
    superadmin: "Superadmin",
    direccion: "Dirección",
    gerente: "Gerencia",
    cabina: "Cabina / Despacho",
    coordinador: "Coordinador",
    paramedico: "Paramédico",
    operador: "Operador",
    medico: "Médico",
    enfermera: "Enfermera",
    finanzas: "Finanzas",
    comercial: "Comercial",
    auditor: "Auditor",
    ambulancia: "Unidad (tablet)",
    cliente: "Cliente Portal",
};

/**
 * Devuelve el "home" del usuario según su rol.
 *  - ambulancia → /crew (CrewMobile)
 *  - cliente    → /portal
 *  - resto      → /dashboard
 */
export function roleHome(user) {
    if (!user) return "/login";
    if (user.role === "cliente") return "/portal";
    if (user.role === "ambulancia") return "/crew";
    return "/dashboard";
}

/**
 * Determina si un rol puede acceder a una ruta del sidebar/sistema interno.
 *
 * La política es de listas blancas: explicitamente declaras qué roles ven cada link.
 * Esto evita filtraciones accidentales cuando agregamos páginas nuevas.
 */
export function canAccess(userRole, allowed) {
    if (!userRole) return false;
    if (!allowed || allowed === "*") return true;  // sin restricción
    if (allowed instanceof Set) return allowed.has(userRole);
    if (Array.isArray(allowed)) return allowed.includes(userRole);
    return false;
}

/**
 * Páginas permitidas para usuarios con rol "ambulancia".
 * Hardcoded por seguridad — solo estos paths son navegables.
 */
export const AMBULANCIA_ALLOWED_PATHS = [
    /^\/crew\/?$/,
    /^\/services\/[a-z0-9-]+$/i,
    /^\/services\/[a-z0-9-]+\/frap$/i,
    /^\/map\/?$/,
];

/**
 * Páginas permitidas para usuarios con rol "cliente".
 * Solo el portal corporativo.
 */
export const CLIENTE_ALLOWED_PATHS = [
    /^\/portal/,
    /^\/nps\//,
];

export function isPathAllowedForRole(path, role) {
    if (role === "ambulancia") {
        return AMBULANCIA_ALLOWED_PATHS.some((re) => re.test(path));
    }
    if (role === "cliente") {
        return CLIENTE_ALLOWED_PATHS.some((re) => re.test(path));
    }
    // Internos: cualquier ruta del sistema interno
    return !path.startsWith("/portal");
}
